import CheckIcon from '@mui/icons-material/Check';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/Download';
import PhotoAlbumIcon from '@mui/icons-material/PhotoAlbum';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box, Link, useTheme } from "@mui/material";
import Grid from '@mui/material/Grid';
import { EventPhoto, Pricelist } from 'common-models';
import { FC, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useAuth } from 'react-oidc-context';
import { useDeleteSingleEventPhotoMutation, useSetEventFrontImageMutation } from 'src/features/events/eventsApiSlice';
import { Configuration } from '../../Configuration';
import { EventGalleryPhotoTools } from './EventGalleryPhotoTools';

type EventGalleryPhotoProps = {
  eventPhoto: EventPhoto;
  loadLazy: boolean;
  isFreeOfCharge: boolean;
  pricelist?: Pricelist;
  isSelectedForDownload: boolean;
  onSelected?: () => void;
  onAddToShoppingCart?: () => void;
  downloadImage: () => Promise<void>;
  selectImageForDownload: () => void;
}

const EventGalleryPhoto: FC<EventGalleryPhotoProps> = ({
  eventPhoto,
  loadLazy,
  isFreeOfCharge,
  pricelist,
  isSelectedForDownload,
  onSelected,
  onAddToShoppingCart,
  downloadImage,
  selectImageForDownload }) => {

  const theme = useTheme();
  const auth = useAuth();

  const iconStyle = {
    marginLeft: '0.2rem',
    marginRight: '0.2rem',
    marginTop: '0.2rem',
    color: theme.palette.background.default,
  };

  const [deleteEventPhoto] = useDeleteSingleEventPhotoMutation();
  const [setEventFrontImage] = useSetEventFrontImageMutation();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isMobileDevice] = useState(isMobile);


  const deleteImage = async () => {
    console.info(`Deleting event photo ${eventPhoto.id}`);
    await deleteEventPhoto({ eventId: eventPhoto.eventId, photoId: eventPhoto.id, accessToken: auth.user?.access_token ?? '' });
  }

  const setPhotoAsFrontImage = async () => {
    setEventFrontImage({ eventId: eventPhoto.eventId, photoId: eventPhoto.id, accessToken: auth.user?.access_token ?? '' });
  }

  const onImageClick = () => {
    if (onSelected !== undefined) {
      onSelected();
    }
  }

  const addToCart = () => {
    setIsAddingToCart(true);
    if (onAddToShoppingCart) {
      onAddToShoppingCart();
    }
    setTimeout(() => setIsAddingToCart(false), 1000);
  }


  const getTools = () => {
    const tools: Array<React.JSX.Element> = [];

    if (auth.isAuthenticated) {
      tools.push(<Link
        key='delete-tool'
        onClick={deleteImage}
        sx={{
          margin: 0,
          padding: 0,
          cursor: 'pointer'
        }}
      >
        <DeleteForeverIcon
          // className={classes.imageToolboxIcon}
          sx={iconStyle}
        />
      </Link>)
      tools.push(<Link
        key='set-as-front-photo-tool'
        onClick={setPhotoAsFrontImage}
        // className={classes.iconLink}
        sx={{
          margin: 0,
          padding: 0,
          cursor: 'pointer'
        }}
      >
        <PhotoAlbumIcon
          // className={classes.imageToolboxIcon}
          sx={iconStyle}
        />
      </Link>)
    }

    if (isFreeOfCharge) {
      if (!isMobileDevice) {
        tools.push(<Link
          key='select-tool'
          onClick={selectImageForDownload}
          // className={classes.imageDownloadButton}
          sx={{
            margin: 0,
            padding: 0,
            cursor: 'pointer'
          }}
        >
          {
            isSelectedForDownload
              ? <CheckBoxIcon sx={iconStyle} />
              : <CheckBoxOutlineBlankIcon sx={iconStyle} />
          }
        </Link>)
      }

      tools.push(<Link
        key='download-tool'
        onClick={downloadImage}
        // className={classes.imageDownloadButton}
        sx={{
          margin: 0,
          padding: 0,
          cursor: 'pointer'
        }}
      >
        <DownloadIcon
          // className={classes.imageToolboxIcon}
          sx={iconStyle}
        />
      </Link>)
    }
    else {
      tools.push(<Link
        key='add-to-cart-tool'
        onClick={addToCart}
        // className={classes.imageDownloadButton}
        sx={{
          margin: 0,
          padding: 0,
          cursor: 'pointer'
        }}
      >
        {
          isAddingToCart
            ? <CheckIcon sx={iconStyle} />
            : <ShoppingCartIcon sx={iconStyle} />
        }
      </Link>)
    }

    return tools;
  }

  return (
    <Grid
      size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
      key={eventPhoto.id}
      sx={{
        padding: theme.spacing(1),
        width: '100%',
        height: '100 %',
        position: 'relative'
      }}
    >
      <Box sx={{
        boxShadow: theme.shadows?.[2] || '0px 1px 3px rgba(0,0,0,0.12)',
        '&:hover': {
          boxShadow: theme.shadows?.[4] || '0px 2px 8px rgba(0,0,0,0.15)',
        },
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape?.borderRadius || 1,
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: theme.palette.primaryLight.main,
        display: 'block',
        left: 0,
        overflow: 'hidden',
        paddingBottom: '100%', // Creates square aspect ratio
        position: 'relative',
        top: 0,
        width: '100%',
      }}>
        <Box sx={{
          zIndex: 100,
          position: 'absolute',
          fontSize: '0.9rem',
          padding: '0.2rem',
          borderBottomRightRadius: theme.shape?.borderRadius || 1,

          backgroundColor: theme.palette.primaryLight.main,

        }}>
          {eventPhoto.id}
        </Box>
        <EventGalleryPhotoTools tools={getTools()} />
        <img
          onClick={onImageClick}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            minWidth: 'auto',
            minHeight: 'auto',
            maxWidth: '100%',
            maxHeight: '100%',
            // objectFit: 'cover',
            padding: '1rem',
            transform: 'translate(-50%, -50%)',

            cursor: onSelected ? 'pointer' : 'default',
          }}
          srcSet={eventPhoto.webSizes.map(y => `${Configuration.StaticSiteUrl}/events/${eventPhoto.eventId}/${eventPhoto.id}-${y.width}.jpg ${y.width}w`).join(', ')}
          sizes="(min-width: 1200px) 530px, (min-width: 992px) 440px, (min-width: 768px) 320px, (min-width: 576px) 230px, 90vw"
          alt={eventPhoto.id}
          title={eventPhoto.id}
          loading='lazy'
        />
      </Box>
    </Grid>
  )
}

export { EventGalleryPhoto };
